import React, { useState } from "react";
import { toast } from "sonner";
import { X, User, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fmtError } from "@/lib/api";

const TYPE_STRUCTURE = [
  ["entreprise_generale", "Entreprise générale du bâtiment"],
  ["syndic", "Syndic de copropriété"],
  ["gestionnaire_patrimoine", "Gestionnaire de patrimoine"],
  ["agence_immo", "Agence immobilière"],
  ["expert_assurance", "Expert en assurance"],
  ["assureur_courtier", "Compagnie ou courtier en assurance"],
  ["maitre_oeuvre", "Maître d'œuvre"],
  ["autre", "Autre"],
];

/** Modal d'activation de la seconde casquette. Rôle cible = ``target``.
 *  Rend les champs manquants selon la casquette demandée. */
export default function EnableRoleModal({ target, onClose, onEnabled }) {
  const { user, enableRole } = useAuth();
  const [prenom, setPrenom] = useState(user?.prenom || "");
  const [nom, setNom] = useState(user?.nom || "");
  const [raisonSociale, setRaisonSociale] = useState(user?.raison_sociale || "");
  const [typeStructure, setTypeStructure] = useState(user?.type_structure || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { role: target };
      if (target === "prestataire") {
        if (!prenom.trim() || !nom.trim()) {
          toast.error("Prénom et nom obligatoires.");
          setBusy(false); return;
        }
        payload.prenom = prenom.trim();
        payload.nom = nom.trim();
      } else {
        if (!raisonSociale.trim()) {
          toast.error("Raison sociale obligatoire.");
          setBusy(false); return;
        }
        if (!typeStructure) {
          toast.error("Sélectionnez un type de structure.");
          setBusy(false); return;
        }
        payload.raison_sociale = raisonSociale.trim();
        payload.type_structure = typeStructure;
      }
      await enableRole(payload);
      toast.success(target === "prestataire"
        ? "Mode prestataire activé — complétez votre profil pour être visible."
        : "Mode donneur d'ordre activé.");
      onEnabled?.(target);
      onClose?.();
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  const Icon = target === "prestataire" ? User : Building2;
  const title = target === "prestataire" ? "Activer le mode prestataire" : "Activer le mode donneur d'ordre";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="enable-role-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-[6px] shadow-lg max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b border-[#E4E7EB]">
          <div className="flex items-center gap-2">
            <Icon size={20} className="text-[#1B3A5C]" />
            <h2 className="font-semibold text-[#1F2933]">{title}</h2>
          </div>
          <button onClick={onClose} className="btn-ghost p-1" data-testid="enable-role-close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="p-4 space-y-3">
          <p className="text-sm text-[#3E4C59]">
            Une seule inscription — deux espaces. Vous pourrez basculer entre les modes à tout moment.
            Votre SIRET, votre e-mail et vos documents restent inchangés.
          </p>

          {target === "prestataire" ? (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="label">Prénom</label>
                  <input required className="input" value={prenom} onChange={(e) => setPrenom(e.target.value)}
                    data-testid="enable-role-prenom" />
                </div>
                <div>
                  <label className="label">Nom</label>
                  <input required className="input" value={nom} onChange={(e) => setNom(e.target.value)}
                    data-testid="enable-role-nom" />
                </div>
              </div>
              <div className="text-xs text-[#3E4C59]">
                Vous pourrez déposer vos documents administratifs (KBIS, RC Pro, décennale…) après activation
                depuis « Mes documents ».
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="label">Raison sociale</label>
                <input required className="input" value={raisonSociale} onChange={(e) => setRaisonSociale(e.target.value)}
                  data-testid="enable-role-raison" />
              </div>
              <div>
                <label className="label">Type de structure</label>
                <select required className="select" value={typeStructure} onChange={(e) => setTypeStructure(e.target.value)}
                  data-testid="enable-role-type-structure">
                  <option value="">— Sélectionnez —</option>
                  {TYPE_STRUCTURE.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                </select>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-[#E4E7EB]">
            <button type="button" onClick={onClose} className="btn-outline" data-testid="enable-role-cancel">Annuler</button>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="enable-role-submit">
              {busy ? "Activation…" : "Activer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
